"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockPolledLiquityStore = void 0;
const constants_1 = require("@ethersproject/constants");
const lib_base_1 = require("@liquity/lib-base");
const _utils_1 = require("./_utils");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
/**
 * Ethers-based {@link @liquity/lib-base#LiquityStore} that updates state whenever there's a new
 * block.
 *
 * @public
 */
class BlockPolledLiquityStore extends lib_base_1.LiquityStore {
    constructor(readable) {
        super();
        this.connection = readable.connection;
        this._readable = readable;
        this._provider = EthersLiquityConnection_1._getProvider(readable.connection);
    }
    async _getRiskiestTroveBeforeRedistribution(assetAddress, overrides) {
        const riskiestTroves = await this._readable.getTroves(assetAddress, { first: 1, sortedBy: "ascendingCollateralRatio", beforeRedistribution: true }, overrides);
        if (riskiestTroves.length === 0) {
            return new lib_base_1.TroveWithPendingRedistribution(constants_1.AddressZero, "nonExistent");
        }
        return riskiestTroves[0];
    }
    async _getCollateralState(assetAddress, spAddress, userAddress, blockTag, blockTimestamp) {
        const start = Date.now();
        console.log(`[BlockPolledLiquityStore] Start fetching collateral state for ${assetAddress}`);
        const logStep = async (label, fn) => {
            const t0 = Date.now();
            const result = await fn();
            const t1 = Date.now();
            console.log(`[BlockPolledLiquityStore]   ${label} took ${t1 - t0}ms`);
            return result;
        };
        const result = await _utils_1.promiseAllValues({
            numberOfTroves: logStep('getNumberOfTroves', () => this._readable.getNumberOfTroves(assetAddress, { blockTag })),
            price: logStep('getPrice', () => this._readable.getPrice(assetAddress, { blockTag })),
            total: logStep('getTotal', () => this._readable.getTotal(assetAddress, { blockTag })),
            totalRedistributed: logStep('getTotalRedistributed', () => this._readable.getTotalRedistributed(assetAddress, { blockTag })),
            slsdInStabilityPool: logStep('getSLSDInStabilityPool', () => this._readable.getSLSDInStabilityPool(spAddress, { blockTag })),
            remainingStabilityPoolPSYReward: lib_base_1.Decimal.ZERO,
            _riskiestTroveBeforeRedistribution: logStep('_getRiskiestTroveBeforeRedistribution', () => this._getRiskiestTroveBeforeRedistribution(assetAddress, { blockTag })),
            _feesFactory: logStep('_getFeesFactory', () => this._readable._getFeesFactory(assetAddress, { blockTag })),
            _feesInNormalMode: logStep('_getFeesInNormalMode', () => this._readable._getFeesFactory(assetAddress, { blockTag }).then(f => f(blockTimestamp, false))),
            ...(userAddress
                ? {
                    balance: logStep('getAssetBalance', () => this._readable.getAssetBalance(assetAddress, userAddress, { blockTag })),
                    allowance: logStep('getAssetAllowance', () => this._readable.getAssetAllowance(assetAddress, userAddress, { blockTag })),
                    collateralSurplusBalance: logStep('getCollateralSurplusBalance', () => this._readable.getCollateralSurplusBalance(assetAddress, userAddress, { blockTag })),
                    troveBeforeRedistribution: logStep('getTroveBeforeRedistribution', () => this._readable.getTroveBeforeRedistribution(assetAddress, userAddress, { blockTag })),
                    stabilityDeposit: logStep('getStabilityDeposit', () => this._readable.getStabilityDeposit(spAddress, userAddress, { blockTag })),
                    isMinted: logStep('getIsMinted', () => this._readable.getIsMinted(assetAddress, userAddress, { blockTag }))
                }
                : {
                    balance: lib_base_1.Decimal.ZERO,
                    allowance: lib_base_1.Decimal.ZERO,
                    collateralSurplusBalance: lib_base_1.Decimal.ZERO,
                    troveBeforeRedistribution: new lib_base_1.TroveWithPendingRedistribution(constants_1.AddressZero, "nonExistent"),
                    stabilityDeposit: new lib_base_1.StabilityDeposit(lib_base_1.Decimal.ZERO, lib_base_1.Decimal.ZERO, lib_base_1.Decimal.ZERO, lib_base_1.Decimal.ZERO
                    // AddressZero
                    ),
                    isMinted: undefined
                })
        });
        const end = Date.now();
        console.log(`[BlockPolledLiquityStore] Done fetching collateral state for ${assetAddress} in ${end - start}ms`);
        return result;
    }
    async _get(blockTag) {
        const start = Date.now();
        console.log(`[BlockPolledLiquityStore] Start _get (full state fetch)`);
        const { userAddress } = this.connection;
        const blockTimestamp = await this._readable._getBlockTimestamp(blockTag);
        const { sfrxEthToken, stabilityPoolSfrxEth, wstEthToken, stabilityPoolWstEth } = this.connection.addresses;
        const sfrxETHBaseState = await this._getCollateralState(sfrxEthToken, stabilityPoolSfrxEth, userAddress, blockTag, blockTimestamp);
        const wstETHBaseState = await this._getCollateralState(wstEthToken, stabilityPoolWstEth, userAddress, blockTag, blockTimestamp);
        const { ...baseState } = await _utils_1.promiseAllValues({
            ...(userAddress
                ? {
                    // accountBalance: this._provider.getBalance(userAddress, blockTag).then(decimalify),
                    slsdBalance: this._readable.getSLSDBalance(userAddress, { blockTag }),
                    psyBalance: lib_base_1.Decimal.ZERO
                }
                : {
                    slsdBalance: lib_base_1.Decimal.ZERO,
                    psyBalance: lib_base_1.Decimal.ZERO
                }),
            collateralStates: {
                sfrxEthToken: sfrxETHBaseState,
                wstEthToken: wstETHBaseState
            }
        });
        const end = Date.now();
        console.log(`[BlockPolledLiquityStore] Done _get (full state fetch) in ${end - start}ms`);
        return [
            {
                ...baseState
            },
            {
                blockTag,
                blockTimestamp,
                _feesFactory: Object.entries(baseState.collateralStates).reduce((acc, [k, v]) => {
                    acc[k] = v._feesFactory;
                    return acc;
                }, {})
            }
        ];
    }
    /** @internal @override */
    _doStart() {
        this._get().then(state => {
            if (!this._loaded) {
                this._load(...state);
            }
        });
        const blockListener = async (blockTag) => {
            const state = await this._get(blockTag);
            if (this._loaded) {
                this._update(...state);
            }
            else {
                this._load(...state);
            }
        };
        this._provider.on("block", blockListener);
        return () => {
            this._provider.off("block", blockListener);
        };
    }
    /** @internal @override */
    _reduceExtra(oldState, stateUpdate) {
        var _a, _b, _c;
        return {
            blockTag: (_a = stateUpdate.blockTag) !== null && _a !== void 0 ? _a : oldState.blockTag,
            blockTimestamp: (_b = stateUpdate.blockTimestamp) !== null && _b !== void 0 ? _b : oldState.blockTimestamp,
            _feesFactory: (_c = stateUpdate._feesFactory) !== null && _c !== void 0 ? _c : oldState._feesFactory
        };
    }
}
exports.BlockPolledLiquityStore = BlockPolledLiquityStore;
//# sourceMappingURL=BlockPolledLiquityStore.js.map